import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@smarty/shared';
import { supabase } from '@/lib/supabase';

export type WaRole = 'supervisor' | 'agent' | null;

export interface WaPermissions {
  loading: boolean;
  /** Rol del usuario en el canal WhatsApp */
  waRole: WaRole;
  /** Puede acceder al inbox */
  canAccessInbox: boolean;
  /** Puede gestionar configuración del provider */
  canManageSettings: boolean;
  /** Es supervisor (ve todas las conversaciones, puede reasignar) */
  isSupervisor: boolean;
  /** Es agente (ve solo las asignadas + sin asignar) */
  isAgent: boolean;
  /** Rol en staff_profiles (owner, manager, employee, cashier) */
  staffRole: string | null;
}

type Resueltos = Omit<WaPermissions, 'loading'>;

const VACIO: Resueltos = {
  waRole: null,
  canAccessInbox: false,
  canManageSettings: false,
  isSupervisor: false,
  isAgent: false,
  staffRole: null,
};

/**
 * Hook que resuelve los permisos del usuario actual para el módulo WhatsApp.
 * Consulta la vista `wa_my_permissions` que combina staff_profiles + staff_channel_roles.
 * Owner/Manager siempre tienen acceso como supervisor.
 *
 * ⭐ Va por la caché (ADR-0012, paso 3) y no por `useEffect`. Este hook lo monta el **Sidebar**,
 * o sea que corría en CADA navegación del panel: un round-trip fijo antes de poder decidir si
 * mostrar el módulo. Con la caché, la primera pantalla lo paga y las demás lo leen de memoria.
 *
 * `staleTime` largo a propósito: los permisos de un usuario cambian cuando un supervisor los
 * cambia, no cada minuto. Revalidar seguido gastaría una consulta por navegación para confirmar
 * lo mismo. Si un permiso cambia en caliente, se refleja al recargar o al volver a la pestaña.
 */
export function useWaPermissions(): WaPermissions {
  const { tenantId, user, userRole } = useAuth();
  const habilitado = !!tenantId && !!user;

  const query = useQuery({
    queryKey: ['wa-permissions', tenantId, user?.id, userRole],
    enabled: habilitado,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Resueltos> => {
      const { data, error } = await supabase
        .from('wa_my_permissions')
        .select('staff_role, wa_role, wa_active, can_manage_settings')
        .eq('tenant_id', tenantId!)
        .maybeSingle();

      // 🪤 El fallback se devuelve, NO se lanza. Si esto tirara el error, la caché marcaría la
      // consulta como fallida y el Sidebar dejaría a un owner sin WhatsApp por un hipo de red.
      // La regla del negocio es que owner/manager siempre tienen acceso completo: eso es una
      // RESPUESTA válida, no un error.
      if (error || !data) {
        const esAdmin = userRole === 'owner' || userRole === 'manager';
        return {
          waRole: esAdmin ? 'supervisor' : null,
          canAccessInbox: esAdmin,
          canManageSettings: esAdmin,
          isSupervisor: esAdmin,
          isAgent: false,
          staffRole: userRole ?? null,
        };
      }

      const waRole = (data.wa_active ? data.wa_role : null) as WaRole;
      return {
        waRole,
        canAccessInbox: waRole !== null,
        canManageSettings: !!data.can_manage_settings,
        isSupervisor: waRole === 'supervisor',
        isAgent: waRole === 'agent',
        staffRole: data.staff_role,
      };
    },
  });

  return {
    // 🪤 `isPending` a secas no alcanza: con `enabled: false` se queda en `true` para siempre, y
    // el Sidebar mostraría "cargando permisos" eterno a un usuario sin sesión. La condición de
    // habilitado tiene que entrar en el cálculo.
    loading: habilitado && query.isPending,
    ...(query.data ?? VACIO),
  };
}
